import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import Sidebar from '../layouts/Sidebar';
import Header from '../components/Dashboard/Header';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Tabs from '../components/ui/Tabs';
import Modal from '../components/ui/Modal';
import DataTable from '../components/ui/DataTable';
import { PageLoader } from '../components/ui/Spinner';
import PatientService from '../services/PatientService';

const IDENTITY_OPTIONS = ['Unknown', 'Known', 'Unconscious', 'Paediatric'];

const IDENTIFICATION_FIELDS = [
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'dateOfBirth', label: 'Date of Birth', type: 'date' },
    { key: 'nationality', label: 'Nationality' },
    { key: 'nationalId', label: 'National ID' },
    { key: 'passportNumber', label: 'Passport Number' },
    { key: 'phone', label: 'Phone' },
    { key: 'emergencyContactName', label: 'Emergency Contact Name' },
    { key: 'emergencyContactPhone', label: 'Emergency Contact Phone' },
    { key: 'emergencyContactRelationship', label: 'Relationship' },
];

// Mirrors seha-cbrne-rn's BodyTab.tsx exactly — same colors, same key set, same 0-100
// normalized coordinate system on the same viewBox — so a marker looks identical whether
// viewed on the tablet that placed it or here. Do not recolor to match brand; treat like
// triage/zone colors.
const MARKER_TYPES = [
    { key: 'burn', label: 'Burn', color: '#d9534f' },
    { key: 'penetrating', label: 'Penetrating', color: '#7a49bf' },
    { key: 'wound', label: 'Wound', color: '#2d7bd2' },
    { key: 'tourniquet', label: 'Tourniquet', color: '#e28f22' },
    { key: 'chemical', label: 'Chemical Exposure', color: '#4e9e67' },
    { key: 'radiation', label: 'Radiation Mark', color: '#0f477f' },
    { key: 'biological', label: 'Bio Concern', color: '#289587' },
    { key: 'other', label: 'Other', color: '#7d8791' },
];

function markerColor(type) {
    return MARKER_TYPES.find((m) => m.key === type)?.color || '#7d8791';
}

function markerLabel(type) {
    return MARKER_TYPES.find((m) => m.key === type)?.label || type;
}

const PATIENT_TABS = [
    { value: 'overview', label: 'Overview' },
    { value: 'vitals', label: 'Vitals' },
    { value: 'cbrne', label: 'CBRNE / Decon' },
    { value: 'treatments', label: 'Treatments & Orders' },
    { value: 'markers', label: 'Body Markers' },
    { value: 'handoff', label: 'Handoff' },
    { value: 'audit', label: 'Everything Logged' },
];

const TRIAGE_BADGE = { Red: 'red', Yellow: 'amber', Green: 'green', Black: 'neutral' };
const AUDIT_CATEGORY_BADGE = { Clinical: 'blue', CBRNE: 'red', Transfer: 'amber', System: 'neutral', Security: 'red' };

function Tile({ label, value }) {
    return (
        <div className="methane-tile">
            <span>{label}</span>
            <b>{value ?? '—'}</b>
        </div>
    );
}

function fmt(dt) {
    return dt ? new Date(dt).toLocaleString() : '—';
}

function memberLabel(memberId) {
    return memberId != null ? `Member #${memberId}` : '—';
}

export default function PatientProfile() {
    const { id } = useParams();
    const [patient, setPatient] = useState(null);
    const [vitals, setVitals] = useState([]);
    const [markers, setMarkers] = useState([]);
    const [treatments, setTreatments] = useState([]);
    const [orders, setOrders] = useState([]);
    const [events, setEvents] = useState([]);
    const [tab, setTab] = useState('overview');
    const [loading, setLoading] = useState(true);
    const [editOpen, setEditOpen] = useState(false);
    const [editForm, setEditForm] = useState(null);
    const [saving, setSaving] = useState(false);

    const removeMarker = async (row) => {
        try {
            await PatientService.deleteBodyMarker(id, row.id);
            setMarkers((prev) => prev.filter((m) => m.id !== row.id));
            toast.success('Marker removed.');
        } catch (err) {
            console.log(err);
            toast.error('Failed to remove marker.');
        }
    };

    const openEdit = () => {
        setEditForm({
            identity: patient.identity || 'Unknown',
            firstName: patient.firstName || '',
            lastName: patient.lastName || '',
            dateOfBirth: patient.dateOfBirth || '',
            nationality: patient.nationality || '',
            nationalId: patient.nationalId || '',
            passportNumber: patient.passportNumber || '',
            phone: patient.phone || '',
            emergencyContactName: patient.emergencyContactName || '',
            emergencyContactPhone: patient.emergencyContactPhone || '',
            emergencyContactRelationship: patient.emergencyContactRelationship || '',
        });
        setEditOpen(true);
    };

    const saveEdit = async () => {
        setSaving(true);
        try {
            const res = await PatientService.update(id, editForm);
            if (res.data?.error) {
                toast.error(res.data.message || 'Failed to save.');
                return;
            }
            const p = await PatientService.findById(id);
            setPatient(p.data);
            setEditOpen(false);
            toast.success('Identification saved.');
        } catch (err) {
            console.log(err);
            toast.error('Failed to save identification.');
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        const load = async () => {
            try {
                const [p, v, m, t, o, e] = await Promise.all([
                    PatientService.findById(id),
                    PatientService.getVitals(id),
                    PatientService.getBodyMarkers(id),
                    PatientService.getTreatments(id),
                    PatientService.getOrders(id),
                    PatientService.getEvents(id),
                ]);
                if (p.error) {
                    toast.error('Failed to load patient.');
                    return;
                }
                setPatient(p.data);
                setVitals(v.data || []);
                setMarkers(m.data || []);
                setTreatments(t.data || []);
                setOrders(o.data || []);
                setEvents(e.data || []);
            } catch (err) {
                console.log(err);
                toast.error('Failed to load patient file.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id]);

    if (loading) {
        return (
            <Sidebar title="Patient File" header={<Header />}>
                <PageLoader label="Loading patient file…" />
            </Sidebar>
        );
    }

    if (!patient) {
        return (
            <Sidebar title="Patient File" header={<Header />}>
                <Card className="text-center text-sm text-neutral-400 py-16 dark:text-neutral-500">
                    Patient record not found.
                </Card>
            </Sidebar>
        );
    }

    const cbrne = patient.cbrne;
    const handoff = patient.handoff;

    return (
        <Sidebar title={`Patient — MRN_${patient.mrn}`} header={<Header />}>
            <Card padded={false} className="overflow-hidden">
                <div className="px-6 py-5 border-b border-neutral-100 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 dark:border-neutral-800">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <Badge variant="neutral">MRN_{patient.mrn}</Badge>
                            <Badge variant={TRIAGE_BADGE[patient.triageCategory] || 'neutral'}>{patient.triageCategory}</Badge>
                            {patient.zone && <Badge variant="blue">{patient.zone}</Badge>}
                            <Badge variant="neutral">{patient.patientStatus}</Badge>
                        </div>
                        <h3 className="font-bold text-xl text-neutral-900 dark:text-neutral-50">
                            {patient.identity === 'Known' && (patient.firstName || patient.lastName)
                                ? [patient.firstName, patient.lastName].filter(Boolean).join(' ')
                                : `${patient.identity || 'Unknown'} patient`}
                            {' '}&middot; {patient.gender || '—'} &middot; {patient.approxAge || '—'}
                        </h3>
                        <p className="text-sm text-neutral-500 mt-1 dark:text-neutral-500">
                            Registered {fmt(patient.registeredAt)}
                            {patient.identity !== 'Known' && ' · No personal data shown — identified by MRN only.'}
                        </p>
                    </div>
                    <Button variant="secondary" onClick={openEdit}>Edit Identification</Button>
                </div>

                <div className="p-6">
                    <Tabs tabs={PATIENT_TABS} active={tab} onChange={setTab} className="mb-6" />

                    {tab === 'overview' && (
                        <div className="space-y-8">
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-y-6 gap-x-6">
                                <Tile label="Heart Rate" value={patient.heartRate != null ? `${patient.heartRate} bpm` : null} />
                                <Tile label="SpO₂" value={patient.spo2 != null ? `${patient.spo2}%` : null} />
                                <Tile label="Temperature" value={patient.temperatureC != null ? `${patient.temperatureC}°C` : null} />
                                <Tile label="Blood Pressure" value={patient.bp} />
                                <Tile label="Respiratory Rate" value={patient.rr} />
                                <Tile label="EtCO₂" value={patient.etco2} />
                                <Tile label="AVPU / GCS" value={patient.avpuGcs} />
                                <Tile label="Vitals Method" value={patient.vitalsMethod} />
                                <Tile label="Vitals Recorded" value={fmt(patient.vitalsRecordedAt)} />
                                <Tile label="CBRNE Agent" value={patient.cbrneAgent || 'Unknown'} />
                                <Tile label="CBRNE Status" value={patient.cbrneStatus} />
                                <Tile label="Decon Status" value={patient.deconStatus} />
                                <Tile label="Destination" value={patient.destination} />
                                <div className="col-span-2 sm:col-span-3 lg:col-span-5 methane-tile">
                                    <span>Note</span>
                                    <b>{patient.note || '—'}</b>
                                </div>
                            </div>

                            {patient.identity === 'Known' && (
                                <div className="pt-6 border-t border-neutral-100 dark:border-neutral-800">
                                    <p className="kpi-label mb-4">Identification — Known Patient</p>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-y-6 gap-x-6">
                                        <Tile label="Name" value={[patient.firstName, patient.lastName].filter(Boolean).join(' ') || null} />
                                        <Tile label="Date of Birth" value={patient.dateOfBirth} />
                                        <Tile label="Nationality" value={patient.nationality} />
                                        <Tile label="National ID" value={patient.nationalId} />
                                        <Tile label="Passport Number" value={patient.passportNumber} />
                                        <Tile label="Phone" value={patient.phone} />
                                        <Tile label="Emergency Contact" value={patient.emergencyContactName} />
                                        <Tile label="Emergency Contact Phone" value={patient.emergencyContactPhone} />
                                        <Tile label="Relationship" value={patient.emergencyContactRelationship} />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {tab === 'vitals' && (
                        <DataTable
                            headCells={[
                                { id: 'recordedAt', label: 'Recorded At', render: (r) => fmt(r.recordedAt) },
                                { id: 'hr', label: 'HR' },
                                { id: 'spo2', label: 'SpO₂' },
                                { id: 'rr', label: 'RR' },
                                { id: 'bp', label: 'BP' },
                                { id: 'temp', label: 'Temp' },
                                { id: 'etco2', label: 'EtCO₂' },
                                { id: 'avpuGcs', label: 'AVPU/GCS' },
                                { id: 'method', label: 'Method' },
                                { id: 'recordedByMemberId', label: 'Recorded By', render: (r) => memberLabel(r.recordedByMemberId) },
                            ]}
                            rows={vitals}
                            emptyLabel="No vitals recorded yet."
                        />
                    )}

                    {tab === 'cbrne' && (
                        <div className="space-y-6">
                            {cbrne ? (
                                <>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-y-6 gap-x-6">
                                        <Tile label="Procedure Type" value={cbrne.procedureType} />
                                        <Tile label="Exposure Source" value={cbrne.source} />
                                        <Tile label="Initial Safety" value={cbrne.initialSafety} />
                                        <Tile label="PPE Zone" value={cbrne.ppeZone} />
                                        <Tile label="Belongings" value={cbrne.belongings} />
                                        <Tile label="Detection Method" value={cbrne.method} />
                                        <Tile label="Device" value={cbrne.device} />
                                        <Tile label="Component" value={cbrne.component} />
                                        <Tile label="Result" value={cbrne.result} />
                                        <Tile label="Reading" value={cbrne.reading} />
                                        <Tile label="Area" value={cbrne.area} />
                                        <Tile label="Decon Type" value={cbrne.deconType} />
                                        <Tile label="Decon" value={cbrne.decon} />
                                        <Tile label="Clothing Removed" value={cbrne.clothingRemoved} />
                                        <Tile label="Patient Tolerance" value={cbrne.patientTolerance} />
                                        <Tile label="Clearance" value={cbrne.clearance} />
                                        <Tile label="Isolation" value={cbrne.isolation} />
                                        <Tile label="Disposition" value={cbrne.disposition} />
                                    </div>
                                    <div>
                                        <p className="kpi-label mb-3">Decontamination Steps</p>
                                        <div className="flex flex-wrap gap-2">
                                            <Badge variant={cbrne.cleaningStep1 ? 'green' : 'neutral'}>1. Immediate/Gross {cbrne.cleaningStep1 ? '✓' : ''}</Badge>
                                            <Badge variant={cbrne.cleaningStep2 ? 'green' : 'neutral'}>2. Secondary/Targeted {cbrne.cleaningStep2 ? '✓' : ''}</Badge>
                                            <Badge variant={cbrne.cleaningStep3 ? 'green' : 'neutral'}>3. Final Rinse {cbrne.cleaningStep3 ? '✓' : ''}</Badge>
                                        </div>
                                    </div>
                                    {cbrne.note && (
                                        <div className="methane-tile">
                                            <span>Note</span>
                                            <b>{cbrne.note}</b>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="strategy-card text-center text-sm text-neutral-400 py-10 dark:text-neutral-500">
                                    No CBRNE/decon record yet.
                                </div>
                            )}
                        </div>
                    )}

                    {tab === 'treatments' && (
                        <div className="space-y-6">
                            <DataTable
                                title="Treatments"
                                headCells={[
                                    { id: 'name', label: 'Treatment' },
                                    { id: 'applied', label: 'Applied', render: (r) => <Badge variant={r.applied ? 'green' : 'neutral'}>{r.applied ? 'Yes' : 'No'}</Badge> },
                                    { id: 'appliedAt', label: 'Applied At', render: (r) => fmt(r.appliedAt) },
                                    { id: 'appliedByMemberId', label: 'Applied By', render: (r) => memberLabel(r.appliedByMemberId) },
                                ]}
                                rows={treatments}
                                emptyLabel="No treatments logged yet."
                            />
                            <DataTable
                                title="Orders"
                                headCells={[
                                    { id: 'category', label: 'Category' },
                                    { id: 'name', label: 'Name' },
                                    { id: 'route', label: 'Route' },
                                    { id: 'dose', label: 'Dose', render: (r) => `${r.dose || ''} ${r.unit || ''}`.trim() || '—' },
                                    { id: 'administeredAt', label: 'Administered At', render: (r) => fmt(r.administeredAt) },
                                    { id: 'givenByMemberId', label: 'Given By', render: (r) => memberLabel(r.givenByMemberId) },
                                ]}
                                rows={orders}
                                emptyLabel="No orders logged yet."
                            />
                        </div>
                    )}

                    {tab === 'markers' && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div className="lg:col-span-1">
                                <div className="border border-neutral-200 rounded-xl bg-white overflow-hidden dark:bg-ink-900 dark:border-neutral-700">
                                    <svg viewBox="0 0 200 420" className="w-full h-auto max-h-[480px]">
                                        {/* Each limb: an outline-color line drawn slightly wider, then a fill-color line
                                            on top drawn slightly narrower — gives a clean ~2px border without needing a
                                            hand-built limb path. Legs/arms first so the torso and head layer cleanly over
                                            the shoulder/hip joins. */}
                                        <line x1="88" y1="183" x2="80" y2="375" stroke="#b9d3e2" strokeWidth="30" strokeLinecap="round" />
                                        <line x1="112" y1="183" x2="120" y2="375" stroke="#b9d3e2" strokeWidth="30" strokeLinecap="round" />
                                        <line x1="68" y1="78" x2="45" y2="230" stroke="#b9d3e2" strokeWidth="24" strokeLinecap="round" />
                                        <line x1="132" y1="78" x2="155" y2="230" stroke="#b9d3e2" strokeWidth="24" strokeLinecap="round" />
                                        <line x1="88" y1="183" x2="80" y2="375" stroke="#f2f8fb" strokeWidth="26" strokeLinecap="round" />
                                        <line x1="112" y1="183" x2="120" y2="375" stroke="#f2f8fb" strokeWidth="26" strokeLinecap="round" />
                                        <line x1="68" y1="78" x2="45" y2="230" stroke="#f2f8fb" strokeWidth="20" strokeLinecap="round" />
                                        <line x1="132" y1="78" x2="155" y2="230" stroke="#f2f8fb" strokeWidth="20" strokeLinecap="round" />
                                        <line x1="100" y1="40" x2="100" y2="70" stroke="#b9d3e2" strokeWidth="18" strokeLinecap="round" />
                                        <line x1="100" y1="40" x2="100" y2="70" stroke="#f2f8fb" strokeWidth="14" strokeLinecap="round" />
                                        <path d="M65,70 C65,68 70,65 78,65 L122,65 C130,65 135,68 135,70 L128,170 C127,178 120,182 100,182 C80,182 73,178 72,170 Z" fill="#f2f8fb" stroke="#b9d3e2" strokeWidth="2" />
                                        <circle cx="100" cy="32" r="20" fill="#f2f8fb" stroke="#b9d3e2" strokeWidth="2" />
                                        {markers.filter((m) => m.x != null && m.y != null).map((m) => (
                                            <circle
                                                key={m.id}
                                                cx={(m.x / 100) * 200}
                                                cy={(m.y / 100) * 420}
                                                r="7"
                                                fill={markerColor(m.type)}
                                                stroke="#fff"
                                                strokeWidth="2"
                                            >
                                                <title>{markerLabel(m.type)}{m.note ? ` — ${m.note}` : ''}</title>
                                            </circle>
                                        ))}
                                    </svg>
                                </div>
                                <div className="flex flex-wrap gap-2 mt-4">
                                    {MARKER_TYPES.map((t) => (
                                        <span key={t.key} className="inline-flex items-center gap-1.5 text-xs font-medium text-neutral-600 dark:text-neutral-300">
                                            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: t.color }} />
                                            {t.label}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div className="lg:col-span-2">
                                <DataTable
                                    headCells={[
                                        { id: 'type', label: 'Type', render: (r) => markerLabel(r.type) },
                                        { id: 'note', label: 'Note' },
                                        { id: 'position', label: 'Position', render: (r) => `${r.x.toFixed(0)}%, ${r.y.toFixed(0)}%` },
                                        { id: 'createdByMemberId', label: 'Logged By', render: (r) => memberLabel(r.createdByMemberId) },
                                    ]}
                                    rows={markers}
                                    onDelete={removeMarker}
                                    emptyLabel="No body markers logged yet."
                                />
                            </div>
                        </div>
                    )}

                    {tab === 'handoff' && (
                        <div className="space-y-6">
                            {handoff ? (
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-y-6 gap-x-6">
                                    <Tile label="Disposition" value={handoff.disposition} />
                                    <Tile label="Destination" value={handoff.destination} />
                                    <Tile label="Transfer Priority" value={handoff.transferPriority} />
                                    <Tile label="Transport Mode" value={handoff.transportMode} />
                                    <Tile label="Receiving Team" value={handoff.receivingTeam} />
                                    <Tile label="Acceptance" value={handoff.acceptance} />
                                    <Tile label="Accepted By" value={memberLabel(handoff.acceptedByMemberId)} />
                                    <Tile label="Accepted At" value={fmt(handoff.acceptedAt)} />
                                    <Tile label="Package Status" value={handoff.packageStatus} />
                                    <Tile label="QR Status" value={handoff.qrStatus} />
                                    <Tile label="NFC Status" value={handoff.nfcStatus} />
                                    <div className="col-span-2 sm:col-span-3 lg:col-span-4 methane-tile">
                                        <span>Transfer Note</span>
                                        <b>{handoff.transferNote || '—'}</b>
                                    </div>
                                </div>
                            ) : (
                                <div className="strategy-card text-center text-sm text-neutral-400 py-10 dark:text-neutral-500">
                                    No handoff record yet.
                                </div>
                            )}
                        </div>
                    )}

                    {tab === 'audit' && (
                        <div className="space-y-2.5">
                            {events.length === 0 && (
                                <div className="strategy-card text-center text-sm text-neutral-400 py-10 dark:text-neutral-500">
                                    No events logged yet.
                                </div>
                            )}
                            {events.map((entry) => (
                                <div key={entry.id} className="timeline-row">
                                    <time>{new Date(entry.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</time>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <Badge variant={AUDIT_CATEGORY_BADGE[entry.category] || 'neutral'}>{entry.category}</Badge>
                                            <p className="text-sm font-bold text-neutral-900 dark:text-neutral-50">{entry.action || 'Event'}</p>
                                        </div>
                                        <p className="text-xs text-neutral-600 mt-1 dark:text-neutral-300">{entry.message}</p>
                                        <p className="text-xs text-neutral-500 mt-0.5 dark:text-neutral-400">
                                            {memberLabel(entry.memberId)} &middot; {fmt(entry.createdAt)}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Card>

            <Modal
                open={editOpen}
                onClose={() => setEditOpen(false)}
                title="Edit Identification"
                subtitle="Only meaningfully needed once identity is set to Known — never shown on list/board views."
                size="lg"
                confirmLabel={saving ? 'Saving…' : 'Save'}
                onConfirm={saveEdit}
            >
                {editForm && (
                    <div className="space-y-5">
                        <div>
                            <label className="form-label">Identity Status</label>
                            <select
                                className="form-input"
                                value={editForm.identity}
                                onChange={(e) => setEditForm({ ...editForm, identity: e.target.value })}
                            >
                                {IDENTITY_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            {IDENTIFICATION_FIELDS.map((f) => (
                                <div key={f.key}>
                                    <label className="form-label">{f.label}</label>
                                    <input
                                        type={f.type || 'text'}
                                        className="form-input"
                                        value={editForm[f.key]}
                                        onChange={(e) => setEditForm({ ...editForm, [f.key]: e.target.value })}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </Modal>
        </Sidebar>
    );
}
